import { RefetchOptions, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  useGetOnboardingProgress,
  QUERY_KEY,
  ApiResponse_GetOnboardingProgress_PropertyManager,
} from '@/api/endpoints/propertyManager/useGetOnboardingProgress';
import { AccountType } from '@/api/enums/supabase';
import { OnboardingProgressWithAccountTypeModel } from '@/api/models/propertyManager';
import { PMPRoutes, publicRoutes } from '@/config/routes';
import { isAssociaSubdomain } from '@/utils/communities';
import { getCurrentOnboardingStep } from '@/utils/onboarding';
import { isSingleFamilyEquivalentType } from '@/utils/propertyLocationTypeUtils';
import { isPathPartOfRoutes } from '@/utils/router';

import ErrorStateWithMuiPageWrapper from '@/components/ErrorStateWithMuiPageWrapper';
import LoadingStateWithMuiPageWrapper from '@/components/LoadingStateWithMuiPageWrapper';

import { usePropertyManager } from './PropertyManagerProvider';

type OnboardingProgressContextType = {
  isFetching: boolean;
  onboardingProgress: OnboardingProgressWithAccountTypeModel;
  isOnboardingComplete?: boolean;
  isMenuTourGuideOpen: boolean;
  setIsMenuTourGuideOpen: (flag: boolean) => void;
  refetch: (options?: RefetchOptions) => void;
  setOnboardingProgress: (
    progress: Partial<ApiResponse_GetOnboardingProgress_PropertyManager>
  ) => void;
};

const OnboardingProgressContext = createContext<OnboardingProgressContextType | undefined>(
  undefined
);

const OnboardingProgressProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { selectedPropertyLocation } = usePropertyManager();
  const recentlyRedirectedFor = useRef<number>();
  const queryClient = useQueryClient();

  const location = useLocation();
  const history = useHistory();

  // Extract campaign from query parameters
  const urlParams = new URLSearchParams(location.search);
  const campaign = urlParams.get('campaign');

  const [isMenuTourGuideOpen, setIsMenuTourGuideOpen] = useState(false);

  const isSingleFamilyLocation = useMemo(
    () =>
      selectedPropertyLocation
        ? isSingleFamilyEquivalentType(selectedPropertyLocation.type)
        : false,
    [selectedPropertyLocation]
  );

  const getOnboardingProgressQuery = useGetOnboardingProgress(selectedPropertyLocation?.id);

  const setOnboardingProgress = useCallback(
    (progress: Partial<ApiResponse_GetOnboardingProgress_PropertyManager>) => {
      queryClient.setQueryData(QUERY_KEY(selectedPropertyLocation?.id), {
        ...getOnboardingProgressQuery.data,
        ...progress,
      });
    },
    [getOnboardingProgressQuery, queryClient, selectedPropertyLocation?.id]
  );

  const onboardingProgress: OnboardingProgressWithAccountTypeModel = useMemo(
    () => ({
      // All users are property managers now
      hasAccountType: true,
      accountType: AccountType.property_manager,
      ...getOnboardingProgressQuery.data,
      firstPropertyLocationId:
        selectedPropertyLocation?.id ?? getOnboardingProgressQuery.data?.firstPropertyLocationId,
      hasPropertyLocation:
        !!selectedPropertyLocation?.id || getOnboardingProgressQuery.data?.hasPropertyLocation,
    }),
    [getOnboardingProgressQuery.data, selectedPropertyLocation]
  );

  const isOnboardingComplete = useMemo(
    () =>
      // All users are property managers, simplified logic
      (!!selectedPropertyLocation?.id || getOnboardingProgressQuery.data?.hasPropertyLocation) &&
      ((isSingleFamilyLocation && getOnboardingProgressQuery.data?.hasEmailAddress) ||
        (!isSingleFamilyLocation &&
          getOnboardingProgressQuery.data?.hasPhoneNumber &&
          getOnboardingProgressQuery.data?.hasBuilding &&
          getOnboardingProgressQuery.data?.hasUnit &&
          getOnboardingProgressQuery.data?.hasTenant)),
    [getOnboardingProgressQuery.data, selectedPropertyLocation, isSingleFamilyLocation]
  );

  const { isFetching, isLoading, isError, refetch } = getOnboardingProgressQuery;

  useEffect(() => {
    if (isOnboardingComplete) return;
    if (!selectedPropertyLocation) return;
    if (recentlyRedirectedFor.current === selectedPropertyLocation.id) return;
    if (isAssociaSubdomain()) return;
    if (isFetching) return;
    if (isPathPartOfRoutes(location.pathname, publicRoutes)) return;
    
    // Don't redirect if already on a property manager route
    // This prevents redirecting from /property-manager/calendar to /property-manager
    if (location.pathname.startsWith('/property-manager')) return;

    recentlyRedirectedFor.current = selectedPropertyLocation.id;

    return history.replace(...getCurrentOnboardingStep(onboardingProgress, campaign));
  }, [
    isFetching,
    history,
    onboardingProgress,
    location,
    selectedPropertyLocation,
    isOnboardingComplete,
    campaign,
  ]);

  const value = useMemo(
    () => ({
      isFetching,
      onboardingProgress,
      isOnboardingComplete,
      refetch,
      setOnboardingProgress,
      isMenuTourGuideOpen,
      setIsMenuTourGuideOpen,
    }),
    [
      isFetching,
      isOnboardingComplete,
      onboardingProgress,
      refetch,
      setOnboardingProgress,
      isMenuTourGuideOpen,
    ]
  );

  const shouldShowLoadingState = useMemo(
    () => isLoading && !isPathPartOfRoutes(location.pathname, [PMPRoutes.onboardingChecklist]),
    [isLoading, location.pathname]
  );

  const content = useMemo(() => {
    if (isError) return <ErrorStateWithMuiPageWrapper />;

    if (shouldShowLoadingState) return <LoadingStateWithMuiPageWrapper />;

    return children;
  }, [isError, shouldShowLoadingState, children]);

  return (
    <OnboardingProgressContext.Provider value={value}>{content}</OnboardingProgressContext.Provider>
  );
};

export const useOnboardingProgress = () => {
  const context = useContext(OnboardingProgressContext);

  if (context === undefined) {
    throw new Error('useOnboardingProgress must be used within an OnboardingProgressProvider');
  }

  return context;
};

export default OnboardingProgressProvider;
